import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import {
  Sparkle, X, PaperPlaneRight, SpeakerHigh, Stop, Translate, ArrowUpRight, ChatCircleDots,
} from "@phosphor-icons/react";
import { toast } from "sonner";

// Floating "Ask NaturePulse" — an always-available AI eco educator, grounded
// on the user's saved Pulse location so answers reference real numbers.
// Uses browser SpeechSynthesis for TTS and reuses /api/translate for i18n.

const LOC_KEY = "np:location:v1";
const HIST_KEY = "np:ask:history:v1";
const MAX_TURNS = 24;

const QUICK_ASKS = [
  "What does AQI 100 mean?",
  "Why is the air worse today near me?",
  "How can I cut my plastic use this week?",
  "Explain climate change to my child",
  "Why do coral reefs bleach?",
  "Give me one impactful action for today",
];

function loadHistory() {
  try { return JSON.parse(localStorage.getItem(HIST_KEY) || "[]"); } catch { return []; }
}
function saveHistory(list) {
  try { localStorage.setItem(HIST_KEY, JSON.stringify(list.slice(-MAX_TURNS))); } catch {}
}
function loadLocContext() {
  try {
    const raw = localStorage.getItem(LOC_KEY);
    if (!raw) return null;
    const l = JSON.parse(raw);
    return { location_name: l.name, lat: l.lat, lon: l.lon };
  } catch { return null; }
}

// Browser TTS with per-message active state.
function speak(text, onEnd) {
  if (!("speechSynthesis" in window)) { toast.error("Text-to-speech not supported here."); return null; }
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text.slice(0, 4000));
  u.rate = 1.0; u.pitch = 1.02; u.lang = "en-US";
  u.onend = onEnd; u.onerror = onEnd;
  window.speechSynthesis.speak(u);
  return u;
}
function stopSpeaking() { if ("speechSynthesis" in window) window.speechSynthesis.cancel(); }

export default function AskNaturePulse() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(loadHistory);   // [{role,content,action?,translation?}]
  const [pending, setPending] = useState(false);
  const [input, setInput] = useState("");
  const [speakingIdx, setSpeakingIdx] = useState(-1);
  const [langs, setLangs] = useState([]);
  const [lang, setLang] = useState("en");
  const scrollerRef = useRef(null);

  useEffect(() => { saveHistory(messages); }, [messages]);
  useEffect(() => () => stopSpeaking(), []);
  useEffect(() => {
    if (langs.length) return;
    http.get("/translate/languages").then((r) => setLangs(r.data?.languages || [])).catch(() => {});
  }, [langs.length]);
  useEffect(() => {
    if (open && scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [open, messages.length, pending]);

  const submit = async (question) => {
    const q = (question ?? input).trim();
    if (!q || pending) return;
    setInput("");
    const nextMessages = [...messages, { role: "user", content: q }];
    setMessages(nextMessages);
    setPending(true);
    try {
      const { data } = await http.post("/ai/ask", {
        question: q,
        context: loadLocContext(),
        history: nextMessages.slice(-6, -1),
      });
      setMessages((m) => [...m, { role: "assistant", content: data.answer, action: data.action }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Assistant unavailable.";
      setMessages((m) => [...m, { role: "assistant", content: `Sorry — ${detail}`, action: null, error: true }]);
    } finally { setPending(false); }
  };

  const onSpeak = (idx, text) => {
    if (speakingIdx === idx) { stopSpeaking(); setSpeakingIdx(-1); return; }
    speak(text, () => setSpeakingIdx(-1));
    setSpeakingIdx(idx);
  };

  const onTranslate = async (idx) => {
    if (lang === "en") return;
    const msg = messages[idx];
    if (!msg || msg.role !== "assistant") return;
    if (msg.translation?.lang === lang) return;
    try {
      const { data } = await http.post("/translate", { text: msg.content, target_lang: lang });
      setMessages((m) => m.map((x, i) => i === idx ? { ...x, translation: { lang, text: data.translated_text } } : x));
    } catch (e) {
      toast.error("Translation failed.");
    }
  };

  const clear = () => { setMessages([]); localStorage.removeItem(HIST_KEY); stopSpeaking(); setSpeakingIdx(-1); };

  return createPortal(
    <>
      {/* Floating action button */}
      <button
        data-testid="ask-fab"
        onClick={() => setOpen(true)}
        aria-label="Ask NaturePulse"
        className="fixed z-[60] right-4 md:right-6 shadow-[0_10px_28px_rgba(184,95,42,.35)] rounded-full flex items-center gap-2 px-4 py-3 font-mono-tech text-[11px] tracking-[0.28em] uppercase bg-gradient-to-br from-[#B85F2A] via-[#D07437] to-[#E4A15C] text-[#FFF9EE] hover:brightness-110 active:brightness-95 transition"
        style={{ bottom: "calc(84px + env(safe-area-inset-bottom, 0px))" }}
      >
        <Sparkle size={16} weight="fill" />
        <span className="hidden xs:inline">Ask NaturePulse</span>
        <span className="xs:hidden">Ask</span>
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="fixed inset-0 z-[70] bg-black/60"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />
            <motion.div
              data-testid="ask-panel"
              className="fixed z-[71] inset-x-0 bottom-0 md:inset-auto md:right-6 md:bottom-6 md:w-[420px] md:h-[640px] max-h-[92dvh] rounded-t-2xl md:rounded-2xl bg-[#F5EBDC] text-[#151312] shadow-[0_-24px_60px_rgba(0,0,0,.4)] flex flex-col overflow-hidden border border-black/10"
              initial={{ y: 500, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 500, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 28 }}
              style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
            >
              {/* Header */}
              <div className="flex items-center justify-between gap-2 px-4 pt-4 pb-3 border-b border-black/10 bg-gradient-to-b from-[#F5EBDC] to-[#FBF2E1]">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-8 h-8 rounded-full bg-gradient-to-br from-[#B85F2A] to-[#E4A15C] text-white grid place-items-center shrink-0">
                    <Sparkle size={14} weight="fill" />
                  </span>
                  <div className="min-w-0">
                    <div className="font-serif-display text-lg leading-tight">Ask NaturePulse</div>
                    <div className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[color:var(--np-muted,#3A3830)]">
                      Friendly eco educator · grounded in your Pulse
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {langs.length > 0 && (
                    <select
                      data-testid="ask-lang"
                      value={lang}
                      onChange={(e) => setLang(e.target.value)}
                      aria-label="Translate answers"
                      className="bg-transparent font-mono-tech text-[10px] tracking-[0.2em] uppercase border border-black/15 rounded-full px-2 py-1"
                    >
                      {langs.slice(0, 24).map((l) => (
                        <option key={l.code} value={l.code}>{l.code.toUpperCase()}</option>
                      ))}
                    </select>
                  )}
                  <button aria-label="Close" data-testid="ask-close" onClick={() => setOpen(false)} className="p-2 hover:bg-black/5 rounded-full">
                    <X size={16} weight="bold" />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 pt-3 pb-2 space-y-3">
                {messages.length === 0 && (
                  <>
                    <div className="rounded-xl border border-dashed border-black/15 p-3 text-[13px] leading-relaxed">
                      Ask anything about air, water, climate, wildlife, or how to live lighter on the planet. I&apos;ll use your saved Pulse location and end with one action you can take today.
                    </div>
                    <div className="grid grid-cols-1 gap-2">
                      {QUICK_ASKS.map((q) => (
                        <button
                          key={q}
                          data-testid="ask-quick"
                          onClick={() => submit(q)}
                          className="text-left px-3 py-2 border border-black/10 rounded-lg hover:border-[color:var(--np-brand,#B85F2A)] font-serif-display text-[15px] leading-snug"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                    <div className="pt-2">
                      <Link
                        to="/faq"
                        onClick={() => setOpen(false)}
                        className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:underline"
                      >
                        Browse the full FAQ <ArrowUpRight size={12} weight="bold" />
                      </Link>
                    </div>
                  </>
                )}

                {messages.map((m, i) => {
                  const isUser = m.role === "user";
                  const display = !isUser && m.translation && m.translation.lang === lang && lang !== "en"
                    ? m.translation.text : m.content;
                  return (
                    <div key={i} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
                      <div
                        className={`max-w-[92%] rounded-2xl px-3.5 py-2.5 ${
                          isUser
                            ? "bg-[#151312] text-[#F5EBDC] rounded-br-md"
                            : m.error
                              ? "bg-red-50 text-red-900 border border-red-200 rounded-bl-md"
                              : "bg-white/85 border border-black/10 rounded-bl-md"
                        }`}
                      >
                        <div className="text-[14px] leading-relaxed whitespace-pre-wrap">{display}</div>
                        {!isUser && m.action && (
                          <div className="mt-2 inline-flex items-start gap-1.5 px-2 py-1 rounded-full bg-gradient-to-r from-[#1B3A2A] to-[#2E5A3A] text-[#F5EBDC] text-[11px] leading-tight">
                            <Sparkle size={11} weight="fill" className="mt-0.5 text-[#E4A15C] shrink-0" />
                            <span>{m.action}</span>
                          </div>
                        )}
                        {!isUser && !m.error && (
                          <div className="mt-2 flex items-center gap-1.5 text-[color:var(--np-muted,#3A3830)]">
                            <button
                              data-testid={`ask-tts-${i}`}
                              onClick={() => onSpeak(i, display)}
                              className="inline-flex items-center gap-1 font-mono-tech text-[9px] tracking-[0.22em] uppercase hover:text-[color:var(--np-text,#151312)]"
                              aria-label={speakingIdx === i ? "Stop reading" : "Read aloud"}
                            >
                              {speakingIdx === i ? <Stop size={11} weight="fill" /> : <SpeakerHigh size={11} weight="fill" />}
                              {speakingIdx === i ? "Stop" : "Listen"}
                            </button>
                            {lang !== "en" && (
                              <button
                                data-testid={`ask-translate-${i}`}
                                onClick={() => onTranslate(i)}
                                className="inline-flex items-center gap-1 font-mono-tech text-[9px] tracking-[0.22em] uppercase hover:text-[color:var(--np-text,#151312)]"
                              >
                                <Translate size={11} weight="fill" /> {lang.toUpperCase()}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {pending && (
                  <div className="flex justify-start">
                    <div className="max-w-[80%] rounded-2xl px-3 py-2 bg-white/85 border border-black/10 rounded-bl-md">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 rounded-full bg-[color:var(--np-brand,#B85F2A)] animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-2 h-2 rounded-full bg-[color:var(--np-brand,#B85F2A)] animate-bounce" style={{ animationDelay: "120ms" }} />
                        <span className="w-2 h-2 rounded-full bg-[color:var(--np-brand,#B85F2A)] animate-bounce" style={{ animationDelay: "240ms" }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Composer */}
              <form
                onSubmit={(e) => { e.preventDefault(); submit(); }}
                className="border-t border-black/10 bg-white/60 backdrop-blur px-3 py-2.5 flex items-center gap-2"
              >
                <input
                  data-testid="ask-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about air, water, wildlife, climate…"
                  className="flex-1 bg-transparent outline-none text-[14px] py-2 px-1 placeholder:text-[color:var(--np-muted,#3A3830)]"
                  autoFocus
                />
                {messages.length > 0 && (
                  <button
                    type="button"
                    onClick={clear}
                    data-testid="ask-clear"
                    className="font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[color:var(--np-muted)] hover:text-[color:var(--np-text)]"
                  >
                    Clear
                  </button>
                )}
                <button
                  type="submit"
                  data-testid="ask-submit"
                  disabled={pending || !input.trim()}
                  className="w-10 h-10 grid place-items-center rounded-full bg-[#151312] text-[#F5EBDC] disabled:opacity-40 hover:opacity-90"
                  aria-label="Send"
                >
                  <PaperPlaneRight size={16} weight="fill" />
                </button>
              </form>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>,
    document.body
  );
}
